import { FormInput } from "components/ProjectNonSpecificComponents/Elements/FormElements/FormInput"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import { REACT_SELECT_FAIL_FALLBACK_OPTIONS } from "components/ProjectSpecificComponents/Constants/GLOBAL_CONSTANTS"
import InfoBubble from "components/ProjectSpecificComponents/Elements/Global/atoms/InfoBubble"
import Select from "components/ProjectSpecificComponents/Elements/Global/atoms/Select"
import { INewRequestFormField } from "components/ProjectSpecificComponents/Interfaces/FormsInterface"
import {
	isReactSelect,
	isReactSelectOptionArray,
	isReactSelectSingleOption,
	TReactSelectOption,
	TReactSelectSelectedOptions,
} from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import { observer } from "mobx-react"
import React, { useMemo } from "react"
import UploadAttachmentsInput from "../../Global/atoms/UploadAttachmentsInput"
import DatePickerInput from "../../Global/atoms/DatePickerInput"
import UserSIdeNewRequestItemOfType from "../atoms/UserSideNewRequestItemOfType"
import { useTranslation } from "react-i18next"
import UserSideNewRequestPickList from "../atoms/UserSideNewRequestPickList"
import { USER_REQ_CLASSNAME } from "../organisms/UserSubmitRequest"

interface Props {
	config: INewRequestFormField
	value?: string | string[] | TReactSelectSelectedOptions
	picklistIdFromCollection?: string | string[] | TReactSelectSelectedOptions
	/**
	 * For use with `fieldType` of 'ATTACHMENT'
	 */
	files?: File[]
	/**
	 * Used for every `fieldType` but 'ATTACHMENT'
	 */
	onChangeValue?: (value: string | readonly TReactSelectOption[] | TReactSelectOption, customKey?: string) => void
	/**
	 * Used for 'ATTACHMENT' `fieldType`
	 */
	onChangeFiles?: (files: File[]) => void
	resizeForTooltip?: boolean
}

const UserSideNewRequestFormInputField = observer(
	({
		config: {
			mainFieldKey,
			charLimit,
			fieldType,
			isMultilineField,
			isRequired,
			nameLV,
			nameEN,
			tooltip,
			pickListValues,
			excludedPickListItemValues,
			picklistId,
			itemOfTypeLevels,
		},
		value,
		picklistIdFromCollection,
		files,
		onChangeValue = () => {},
		onChangeFiles,
	}: Props) => {
		const { isMobile } = useScreenType()
		const isMultiSelect: boolean = fieldType === "MULTI_PICKLIST"
		const { i18n } = useTranslation()
		// ! TECHNICAL DEBT: 3184, replace this with dynamic key ([parsedkey])
		const isEN = i18n.language === "en"

		const parsedValue = useMemo(() => {
			const stringValue = typeof value === "string" ? value : undefined

			const multiSelectVal =
				isReactSelect(value) && isReactSelectOptionArray(value) ? value : undefined

			return { string: stringValue, multiSelect: multiSelectVal }
		}, [value])
		
		const parsedPickValue = useMemo(() => {
			const stringValue = typeof picklistIdFromCollection === "string" ? picklistIdFromCollection : undefined

			const multiSelectVal =
				isReactSelect(picklistIdFromCollection) && isReactSelectOptionArray(picklistIdFromCollection) ? picklistIdFromCollection : undefined

			return { string: stringValue, multiSelect: multiSelectVal }
		}, [picklistIdFromCollection])
		
		const InputComponent = useMemo(() => {
			switch (fieldType) {
				case "DATE":
					return (
						<DatePickerInput
							isRequired={isRequired}
							onChangeDate={(date) => onChangeValue(date)}
							value={parsedValue.string ?? ""}
						/>
					)

				case "NUMBER":
					return (
						<FormInput
							className={isRequired ? USER_REQ_CLASSNAME : ""}
							type="number"
							wide
							value={parsedValue.string ?? ""}
							onValueChange={(text) => onChangeValue(text)}
						/>
					)
				case "ITEM_OF_TYPE":
					return (
						<UserSIdeNewRequestItemOfType
							isRequired={isRequired}
							valueKey={parsedValue.string}
							picklistValue={parsedPickValue.string}
							value={parsedValue.multiSelect}
							options={pickListValues ?? REACT_SELECT_FAIL_FALLBACK_OPTIONS}
							isMultiSelect={isMultiSelect}
							onChangeValue={onChangeValue}
							mainFieldKey={mainFieldKey}
							itemOfTypeLevels={itemOfTypeLevels}
							name={isEN ? nameEN : nameLV}
							tooltipText={isEN ? tooltip?.textEN ?? undefined : tooltip?.textLV ?? undefined}
						/>
					)
				case "MULTI_PICKLIST":
				case "PICKLIST":
					return (
						<UserSideNewRequestPickList
							valueKey={parsedValue.string}
							value={isMultiSelect ? parsedValue.multiSelect ?? [] : parsedValue.multiSelect}
							picklistId={picklistId}
							excludedOptions={excludedPickListItemValues}
							isMultiSelect={isMultiSelect}
							onChangeValue={onChangeValue}
						/>
					)
				case "RICHTEXT":
					return (
						<FormInput
							className={isRequired ? USER_REQ_CLASSNAME : ""}
							charLimit={Number(charLimit) ?? undefined}
							multiline
							wide
							value={parsedValue.string ?? ""}
							onValueChange={(text) => onChangeValue(text)}
						/>
					)
				case "STRING":
					return (
						<FormInput
							className={isRequired ? USER_REQ_CLASSNAME : ""}
							charLimit={Number(charLimit) ?? undefined}
							multiline={isMultilineField}
							wide
							value={parsedValue.string ?? ""}
							onValueChange={(text) => onChangeValue(text)}
						/>
					)
				case "ATTACHMENT":
					return <UploadAttachmentsInput files={files} onChangeFiles={onChangeFiles} />
			}
		}, [fieldType, value, files, pickListValues, isEN])

		return (
			<HyperDiv direction={isMobile ? "column" : "row"} width="100%" spacedEvenly>
				{fieldType !== "ITEM_OF_TYPE" && (
					<p id="form-row-label" className="hide-overflown-text-nowrap w-full">
						{`${isEN ? nameEN : nameLV} ${isRequired ? "*" : ""}`}
					</p>
				)}

				<div id="form-row-input" className="flex flex-row items-end w-full">
					<div className="w-full">{InputComponent}</div>

					{fieldType !== "ITEM_OF_TYPE" && (isEN ? tooltip?.textEN : tooltip?.textLV) && (
						<div className="pb-2.5 pl-2">
							<InfoBubble tooltipText={isEN ? tooltip?.textEN ?? "" : tooltip?.textLV ?? ""} />
						</div>
					)}
				</div>
			</HyperDiv>
		)
	}
)

export default UserSideNewRequestFormInputField
